import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Canvas scratch surface laid over `children`.
 *
 * - Pointer events cover mouse, touch and pen with one code path.
 * - `onScratchStart` fires once, on the first stroke — the parent uses it to
 *   fetch the hidden code while the user is still scratching.
 * - Clearance is sampled on a coarse alpha grid; past `threshold` the foil
 *   fades away and `onReveal` fires once.
 * - `revealed` skips the foil entirely (revisits, or the keyboard button).
 */
const BRUSH = 26;
const SAMPLE_STEP = 12;

const ScratchCard = ({
  children,
  revealed = false,
  threshold = 0.45,
  onScratchStart,
  onReveal,
  label = 'Scratch to reveal',
  className = '',
}) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const lastPoint = useRef(null);
  const drawing = useRef(false);
  const started = useRef(false);
  const done = useRef(revealed);
  const strokes = useRef(0);
  // covered → fading (canvas opacity transition) → gone (canvas unmounted)
  const [phase, setPhase] = useState(revealed ? 'gone' : 'covered');
  const cleared = phase !== 'covered';

  const finish = useCallback(() => {
    if (done.current) return;
    done.current = true;
    setPhase('fading');
    onReveal?.();
  }, [onReveal]);

  useEffect(() => {
    if (phase !== 'fading') return undefined;
    const t = setTimeout(() => setPhase('gone'), 500);
    return () => clearTimeout(t);
  }, [phase]);

  // Revealed from outside (keyboard button / revisit): fade whatever is left.
  useEffect(() => {
    if (revealed && !done.current) {
      done.current = true;
      setPhase('fading');
    }
  }, [revealed]);

  // Paint the foil at the element's real pixel size, and repaint on resize
  // (only while it is still fully covered, so a resize never "heals" scratches).
  const paintFoil = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap || done.current) return;
    const { width, height } = wrap.getBoundingClientRect();
    if (!width || !height) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    // Kuicko orange → purple foil.
    const g = ctx.createLinearGradient(0, 0, width, height);
    g.addColorStop(0, '#FE5F1E');
    g.addColorStop(0.55, '#E0368F');
    g.addColorStop(1, '#9900FF');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);

    // Diagonal sheen stripes.
    ctx.save();
    ctx.globalAlpha = 0.09;
    ctx.fillStyle = '#ffffff';
    for (let x = -height; x < width; x += 22) {
      ctx.beginPath();
      ctx.moveTo(x, height);
      ctx.lineTo(x + height, 0);
      ctx.lineTo(x + height + 8, 0);
      ctx.lineTo(x + 8, height);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();

    // Scattered sparkles — deterministic so repaints don't shimmer.
    ctx.save();
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    for (let i = 0; i < 26; i += 1) {
      const sx = ((i * 97) % 100) / 100 * width;
      const sy = ((i * 53 + 17) % 100) / 100 * height;
      // Keep the label band clear.
      if (Math.abs(sy - height / 2) < 30 && Math.abs(sx - width / 2) < width * 0.28) continue;
      const r = 1 + (i % 3) * 0.7;
      ctx.beginPath();
      ctx.arc(sx, sy, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // Label.
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${Math.max(16, Math.min(22, width / 16))}px Outfit, Inter, system-ui, sans-serif`;
    ctx.fillText(label, width / 2, height / 2 - 8);
    ctx.globalAlpha = 0.85;
    ctx.font = '500 12px Inter, system-ui, sans-serif';
    ctx.fillText('Use your finger or mouse', width / 2, height / 2 + 16);
    ctx.globalAlpha = 1;
  }, [label]);

  useEffect(() => {
    if (cleared) return undefined;
    paintFoil();
    // Fonts may land after first paint; repaint so the label uses Outfit.
    document.fonts?.ready?.then(() => {
      if (strokes.current === 0) paintFoil();
    });
    const ro = new ResizeObserver(() => {
      if (strokes.current === 0) paintFoil();
    });
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [cleared, paintFoil]);

  const pointFrom = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const scratchTo = (p) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = BRUSH * 2;
    ctx.beginPath();
    const from = lastPoint.current || p;
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(p.x + 0.01, p.y + 0.01);
    ctx.stroke();
    lastPoint.current = p;
  };

  const clearedRatio = () => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const step = Math.max(1, Math.round(SAMPLE_STEP * (window.devicePixelRatio || 1)));
    const data = ctx.getImageData(0, 0, width, height).data;
    let total = 0;
    let clear = 0;
    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        total += 1;
        if (data[(y * width + x) * 4 + 3] < 128) clear += 1;
      }
    }
    return total ? clear / total : 0;
  };

  const onPointerDown = (e) => {
    if (done.current) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    lastPoint.current = null;
    if (!started.current) {
      started.current = true;
      onScratchStart?.();
    }
    scratchTo(pointFrom(e));
  };

  const onPointerMove = (e) => {
    if (!drawing.current || done.current) return;
    e.preventDefault();
    scratchTo(pointFrom(e));
    strokes.current += 1;
    if (strokes.current % 8 === 0 && clearedRatio() >= threshold) finish();
  };

  const onPointerUp = () => {
    drawing.current = false;
    lastPoint.current = null;
    if (!done.current && clearedRatio() >= threshold) finish();
  };

  return (
    <div ref={wrapRef} className={`relative overflow-hidden rounded-2xl ${className}`}>
      {children}
      {phase !== 'gone' && (
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={label}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          aria-hidden={cleared || undefined}
          className={`absolute inset-0 h-full w-full touch-none select-none transition-[opacity,transform] duration-500 ease-out ${
            cleared
              ? 'pointer-events-none scale-[1.03] opacity-0'
              : 'cursor-grab active:cursor-grabbing'
          }`}
        />
      )}
    </div>
  );
};

export default ScratchCard;
